/**
 * Feedbackpunten uit een klantmail bij deze taak, als checklist (20 okt 2026). De PL zet ze erin
 * vanuit Correspondentie; de editor vinkt hier af wat verwerkt is (met ongedaan maken).
 * Laadt alleen als de taak punten heeft (taak.feedbackPunten uit Dagstart), of als `altijd` aanstaat.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';

import { meldFout } from '../../utils/apiFout';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { formatTijd, leesFeedbackPunten, zetPuntStatus } from '../../utils/feedbackPunten';

const datumKort = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};

export default function FeedbackPuntenLijst({ taakId, aantal = 0, altijd = false, onAantallen }) {
  const [imports, setImports] = useState(null);
  const [bezig, setBezig] = useState(null); // punt-id dat wordt bijgewerkt

  const melden = useCallback((lijst) => {
    const alle = (lijst || []).flatMap((i) => i.punten);
    onAantallen?.({ totaal: alle.length, open: alle.filter((p) => p.status === 'open').length });
  }, [onAantallen]);

  const laad = useCallback(async () => {
    try {
      const d = await leesFeedbackPunten([taakId]);
      const lijst = d?.[taakId]?.imports || [];
      setImports(lijst);
    } catch (e) {
      // Laden mislukt ≠ geen punten: lijst en telling blijven zoals ze waren (Codex r1).
      meldFout(e, 'Feedbackpunten laden');
    }
  }, [taakId]);

  // Telling naar FeedbackVeld ('feedback aanwezig'), na laden en na elk afvinken.
  useEffect(() => { if (imports) melden(imports); }, [imports, melden]);

  useEffect(() => { setImports(null); }, [taakId]);
  useEffect(() => {
    if (taakId && (altijd || aantal > 0)) laad();
  }, [taakId, aantal, altijd, laad]);

  const zet = async (punt, status) => {
    if (bezig) return;
    setBezig(punt.id);
    const pas = (s) => setImports((lijst) => (lijst || []).map((i) => ({
      ...i, punten: i.punten.map((p) => (p.id === punt.id ? { ...p, status: s } : p)) })));
    try {
      const r = await zetPuntStatus(punt.id, status);
      pas(r.punt.status);
      biedOngedaanAan(status === 'verwerkt' ? 'Punt afgevinkt' : 'Punt weer open', async () => {
        const terug = await zetPuntStatus(punt.id, r.vorige);
        pas(terug.punt.status);
      });
    } catch (e) {
      meldFout(e, 'Punt bijwerken');
      laad();
    } finally {
      setBezig(null);
    }
  };

  if (!imports || !imports.length) return null;

  return (
    <Box data-testid="feedback-punten" sx={{ mb: 1 }}>
      {imports.map((imp) => (
        <Box key={imp.id} sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>
            Mail · {imp.bronLabel || 'klant'}{imp.aangemaaktOp ? ` · toegevoegd ${datumKort(imp.aangemaaktOp)}` : ''}
            {imp.zieMail ? ' · 📎 zie mail voor afbeeldingen' : ''}
          </Typography>
          {imp.punten.map((p) => (
            <Box key={p.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Checkbox size="small" checked={p.status === 'verwerkt'} disabled={bezig === p.id} sx={{ p: 0.25, mt: '1px' }}
                onChange={(e) => zet(p, e.target.checked ? 'verwerkt' : 'open')}
                inputProps={{ 'aria-label': `Verwerkt: ${p.tekst}` }} />
              <Typography sx={{ fontSize: '0.78rem', pt: 0.4, color: p.status === 'verwerkt' ? '#94a3b8' : '#2d3335',
                textDecoration: p.status === 'verwerkt' ? 'line-through' : 'none' }}>
                {p.seconden != null && <Box component="span" sx={{ color: '#64748b', mr: 0.5 }}>{formatTijd(p.seconden)}</Box>}
                {p.tekst}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
